import React from 'react';
import { useRouter } from 'expo-router';
import { msg } from '@lingui/core/macro';

import type { EntitlementSnapshot } from '../../lib/entitlements';
import { getLegacyTransition, getLegacyUpgradeAction, isLegacyAccessEnded } from '../../lib/legacyTransition';
import Banner from '../ui/Banner';

interface ProjectIdentity {
    id: string;
    name?: string | null;
    mode?: string | null;
    paused_at?: string | null;
    visibility?: string | null;
}

interface LegacyAccessEndedBannerProps {
    snapshot: EntitlementSnapshot | null | undefined;
    projects: ProjectIdentity[];
}

export default function LegacyAccessEndedBanner({
    snapshot,
    projects,
}: LegacyAccessEndedBannerProps) {
    const router = useRouter();
    const transition = getLegacyTransition(snapshot);
    const upgradeAction = getLegacyUpgradeAction(snapshot);
    const ended = isLegacyAccessEnded(snapshot);
    const transitionPausedProjects = projects.filter(
        (project) => transition.paused_project_ids.includes(project.id) && project.paused_at
    );

    if (transition.status === 'converted') {
        if (transitionPausedProjects.length === 0) {
            return null;
        }

        return (
            <Banner
                type="info"
                variant="bordered"
                title={msg({
                    id: 'billing.legacyTransition.paidPlanActive',
                    message: 'Your paid plan is active',
                })}
                message={{
                    ...msg({
                        id: 'billing.legacyTransition.convertedPausedProjects',
                        message: '{count, plural, one {# project remains} other {# projects remain}} paused from the Free-plan transition. Nothing was deleted. Resume the projects you want from their Settings pages, up to your plan limit.',
                    }),
                    values: { count: transitionPausedProjects.length },
                }}
            />
        );
    }

    if (!ended) {
        return null;
    }

    const keptProjects = transition.kept_project_ids
        .map((projectId) => projects.find((project) => project.id === projectId))
        .filter((project): project is ProjectIdentity => Boolean(project));
    const activeKeptProjects = keptProjects.filter(
        (project) => project.mode === 'active' && !project.paused_at
    );
    const keptProject = activeKeptProjects[0]
        ?? projects.find((project) => project.mode === 'active' && !project.paused_at);
    const pausedCount = transitionPausedProjects.length;
    const publicizedCount = projects.filter(
        (project) => transition.made_public_project_ids.includes(project.id)
            && project.visibility === 'public'
    ).length;
    const commonValues = {
        pausedCount,
        publicizedCount,
        rssGrandfathered: String(transition.rss_grandfathered),
    };
    const message = transition.status === 'expired_pending'
        ? {
            ...msg({
                id: 'billing.legacyTransition.pendingFreeMessage',
                message: 'Your account is now on Free while its one-project limit is being applied. {publicizedCount, plural, =0 {} one {Its visibility was changed to public. } other {Their visibility was changed to public. }}{rssGrandfathered, select, true {RSS remains available.} other {RSS/Atom delivery is unavailable on this Free account.}} New AI summaries, the supported CLI, and established Forwarded Inbox processing are unavailable on Free.',
            }),
            values: commonValues,
        }
        : pausedCount > 0
            ? {
                ...msg({
                    id: 'billing.legacyTransition.pausedProjectsMessage',
                    message: '{hasProjectName, select, true {{projectName} remains active and public.} other {One project remains active and public.}} {pausedCount, plural, one {# other project is paused and retains its privacy, history, and settings.} other {# other projects are paused and retain their privacy, history, and settings.}} {publicizedCount, plural, =0 {} one {Its visibility was changed to public. } other {Their visibility was changed to public. }}{rssGrandfathered, select, true {RSS remains available.} other {RSS/Atom delivery is unavailable on this Free account.}} New AI summaries, the supported CLI, and established Forwarded Inbox processing are unavailable on Free.',
                    comment: 'projectName is user-authored and must remain unchanged.',
                }),
                values: {
                    ...commonValues,
                    hasProjectName: String(Boolean(keptProject?.name)),
                    projectName: keptProject?.name || '',
                },
            }
            : keptProject
                ? {
                    ...msg({
                        id: 'billing.legacyTransition.keptProjectMessage',
                        message: '{hasProjectName, select, true {{projectName} remains active and public.} other {Your project remains active and public.}} {publicizedCount, plural, =0 {} one {Its visibility was changed to public. } other {Their visibility was changed to public. }}{rssGrandfathered, select, true {RSS remains available.} other {RSS/Atom delivery is unavailable on this Free account.}} New AI summaries, the supported CLI, and established Forwarded Inbox processing are unavailable on Free.',
                        comment: 'projectName is user-authored and must remain unchanged.',
                    }),
                    values: {
                        ...commonValues,
                        hasProjectName: String(Boolean(keptProject.name)),
                        projectName: keptProject.name || '',
                    },
                }
                : {
                    ...msg({
                        id: 'billing.legacyTransition.freeProjectMessage',
                        message: 'Free includes one active public project. {publicizedCount, plural, =0 {} one {Its visibility was changed to public. } other {Their visibility was changed to public. }}{rssGrandfathered, select, true {RSS remains available.} other {RSS/Atom delivery is unavailable on this Free account.}} New AI summaries, the supported CLI, and established Forwarded Inbox processing are unavailable on Free.',
                    }),
                    values: commonValues,
                };

    return (
        <Banner
            type="warning"
            variant="bordered"
            title={transition.status === 'expired_pending'
                ? msg({ id: 'billing.legacyTransition.movingToFree', message: 'Moving your account to Free' })
                : msg({ id: 'billing.legacyTransition.onFreePlan', message: 'You’re on the Free plan' })}
            message={message}
            action={{
                label: upgradeAction.label,
                onPress: () => router.push(upgradeAction.path),
            }}
        />
    );
}
